import * as vp from 'vopay';
import { FunctionComponent } from 'react';
import { Prompt } from 'react-router-dom';
import { Box, Button, Table, TextField, Toggle, SubTitle, Icon } from 'vopay';
import { Formik } from 'formik';
import { usePendingData } from '@/providers/PendingData.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';

type TokenProps = {
  values: vp.AccountPermissions;
  onPermissionSave: (values: vp.AccountPermissions) => void;
  permissionRows: Record<string, vp.PartnerAccountPermissionResponse[]>;
};

type FC = FunctionComponent<TokenProps>;

const PermissionsReview: FC = (props: TokenProps) => {
  const { values, onPermissionSave, permissionRows } = props;
  const { hasPendingData } = usePendingData();
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();

  const sectionNames = Object.keys(permissionRows).sort((a, b) => a.localeCompare(b));

  const formatSectionName = (section: string): string => {
    return section
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handlers = {
    onSave: () => {
      onPermissionSave(values);
    },
  };

  function getTableProps(section: string): vp.TableProps {
    const sectionPermissions = permissionRows[section] || [];

    // Create rows directly as TableProps rows format
    const rows = sectionPermissions.flatMap((permission) => {
      // Skip if permission doesn't exist in values
      if (!(permission.Permission in values)) {
        return [];
      }

      const fieldValue = values[permission.Permission];
      let valueElement = null;

      switch (permission.Type) {
        case 'boolean':
          valueElement = (
            <Toggle
              name={permission.Permission}
              value={Boolean(fieldValue)}
              disabled
              onBlur={() => {}}
              onChange={() => {}}
            />
          );
          break;
        case 'string':
          valueElement = (
            <TextField
              name={permission.Permission}
              id={permission.Permission}
              variant="standard"
              preset={permission.Permission.toLowerCase().includes('amount') ? 'currency' : undefined}
              value={String(fieldValue || '')}
              disabled
              onBlur={() => {}}
              onChange={() => {}}
            />
          );
          break;
        case 'number':
          valueElement = (
            <TextField
              name={permission.Permission}
              id={permission.Permission}
              variant="standard"
              preset="currency"
              value={String(fieldValue || '')}
              disabled
              onBlur={() => {}}
              onChange={() => {}}
            />
          );
          break;
        case 'DATE':
        case 'datetime':
          valueElement = (
            <TextField
              name={permission.Permission}
              id={permission.Permission}
              variant="standard"
              value={String(fieldValue || '')}
              disabled
              elementLeft={<Icon icon="calendar" size="sm" />}
              onBlur={() => {}}
              onChange={() => {}}
            />
          );
          break;
        default:
          return []; // Skip this permission
      }

      // Return the row directly in the final format
      return [
        {
          detailKey: permission.Label,
          detailValue: (
            <Box jc="fe" pt="sm" pb="sm">
              {valueElement}
            </Box>
          ),
        },
      ];
    });

    return {
      name: `account-permissions-review-${section}`,
      variant: 'pair',
      columns: {
        detailKey: { header: '' },
        detailValue: { header: '' },
      },
      rows,
    };
  }

  return (
    <Formik enableReinitialize initialValues={values} onSubmit={handlers.onSave} validateOnChange validateOnBlur>
      {({ handleSubmit }) => (
        <form onSubmit={handleSubmit}>
          <Box flx="col" width="50%" mt="md">
            {sectionNames.map((sectionName) => (
              <Box key={sectionName} flx="col" mb="md">
                <SubTitle subTitle={formatSectionName(sectionName)} fontSize="16px" fontWeight="bold" />
                <Table {...getTableProps(sectionName)} />
              </Box>
            ))}
          </Box>
          <Box width="100%" jc="fe" mt="lg">
            <Button id="sub-account-creation-btn" label="Save" type="submit" isDisabled={isRoleViewOnly} />
          </Box>
          <Prompt when={hasPendingData} message="You have unsaved account data that will be discarded." />
        </form>
      )}
    </Formik>
  );
};

export default PermissionsReview;
